// ============================================================================
// ARCUSCONNECT — Thème centralisé (design "Ardoise + Ligne")
// ----------------------------------------------------------------------------
// Source unique de vérité pour les couleurs, espacements et styles partagés.
// Importer depuis n'importe quel composant :  import { theme } from '../../theme';
// (adapter le chemin relatif selon l'emplacement du composant)
// ============================================================================

export const theme = {
  // ── Couleurs de marque ────────────────────────────────────────────────
  orange:       '#F18A00',   // accent principal Arcus
  orangeSoft:   '#F4A94E',   // orange adouci (hover, textes sur fond sombre)
  orangeFaint:  'rgba(241,138,0,0.12)', // voile orange (fonds très légers)
  black:        '#161616',   // noir charte (titres forts, boutons secondaires)

  // ── Sidebar (ardoise) ─────────────────────────────────────────────────
  sidebarBg:        '#2B3440',   // fond sidebar ardoise
  sidebarHover:     '#323B47',   // survol d'un item
  sidebarDivider:   '#3A4654',   // séparateurs internes sidebar
  sidebarText:      '#C3D0DD',   // texte des items inactifs
  sidebarTextMuted: '#9FB0C3',   // sous-items / libellés discrets
  sidebarLabel:     '#7E8EA0',   // titres de section (PORTEFEUILLE, etc.)
  sidebarActive:    '#FFFFFF',   // texte de l'item actif

  // ── Surfaces (zone de contenu) ────────────────────────────────────────
  bg:           '#F7F8FA',   // fond de page
  surface:      '#FFFFFF',   // cartes / panneaux
  surfaceAlt:   '#FAFBFC',   // surface secondaire (entêtes de tableau, etc.)

  // ── Texte ─────────────────────────────────────────────────────────────
  text:         '#1A1F26',   // texte principal
  textSecondary:'#5A6573',   // texte secondaire
  textTertiary: '#8A95A3',   // hints / métadonnées
  textOnDark:   '#FFFFFF',

  // ── Bordures / filets (style "Ligne") ─────────────────────────────────
  border:       '#E6E9ED',   // filet standard
  borderStrong: '#CDD3DA',   // filet d'emphase (sous-titres, en-têtes)
  borderDark:   '#1A1F26',   // trait noir net (soulignement de titre de page)

  // ── Rayons & espacements ──────────────────────────────────────────────
  radiusSm: 6,
  radius:   8,
  radiusLg: 12,
  radiusPill: 999,

  // ── Statuts dossiers (texte + fond pâle pour badges) ──────────────────
  // Conserve la sémantique métier des 8 statuts (cohérent avec OutilSinistre).
  statut: {
    en_attente_informations: { fg: '#C2410C', bg: '#FFF3E6', dot: '#EA580C', label: 'En attente des informations' },
    en_cours_traitement:     { fg: '#1D4ED8', bg: '#EFF6FF', dot: '#3B82F6', label: 'En cours de traitement' },
    attente_validation:      { fg: '#6D28D9', bg: '#F5F3FF', dot: '#8B5CF6', label: 'En attente de validation' },
    attente_paiement_solde:  { fg: '#0F766E', bg: '#F0FDFA', dot: '#14B8A6', label: 'Attente paiement du solde' },
    solde_paye:              { fg: '#0E7490', bg: '#ECFEFF', dot: '#06B6D4', label: 'Solde payé' },
    attente_paiement_compta: { fg: '#854D0E', bg: '#FEFCE8', dot: '#CA8A04', label: 'En attente paiement comptabilité' },
    paiement_realise:        { fg: '#065F46', bg: '#F0FDF4', dot: '#10B981', label: 'Paiement réalisé' },
    decline:                 { fg: '#475569', bg: '#F1F5F9', dot: '#94A3B8', label: 'Dossier décliné' },
  },
};

// ── Helpers réutilisables ───────────────────────────────────────────────

// Style d'un badge de statut à fond pâle (pour les en-têtes de fiche).
export function statutBadgeStyle(statut) {
  const s = theme.statut[statut] || theme.statut.en_cours_traitement;
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 12, fontWeight: 500, padding: '4px 11px',
    borderRadius: theme.radiusPill, color: s.fg, background: s.bg,
  };
}

// Style d'une puce de statut (point coloré + texte) pour les listes denses.
export function statutDotStyle(statut) {
  const s = theme.statut[statut] || theme.statut.en_cours_traitement;
  return { color: s.fg, fontWeight: 500, fontSize: 12.5, whiteSpace: 'nowrap' };
}

// Couleur du point seul (pour rendre la puce "● Texte").
export function statutDotColor(statut) {
  const s = theme.statut[statut] || theme.statut.en_cours_traitement;
  return s.dot;
}

// Style du titre de page souligné d'un trait noir net (style "Ligne").
export const pageTitleStyle = {
  display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
  borderBottom: `2px solid ${theme.borderDark}`, paddingBottom: 10, marginBottom: 4,
};

// Style du texte de titre lui-même (gros et gras, aligné sur ListeSinistres).
export const pageTitleTextStyle = {
  fontSize: 24, fontWeight: 800, color: theme.text,
  display: 'flex', alignItems: 'center', gap: 10, margin: 0,
};

// Style d'un onglet de filtre / de section souligné d'orange à l'état actif.
export function tabStyle(active) {
  return {
    fontSize: 13, cursor: 'pointer', paddingBottom: 7,
    color: active ? theme.text : theme.textSecondary,
    borderBottom: active ? `2px solid ${theme.orange}` : '2px solid transparent',
    fontWeight: active ? 500 : 400, transition: 'color .15s',
  };
}

// Bouton primaire orange.
export const primaryButtonStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 13, fontWeight: 500, padding: '8px 16px',
  borderRadius: theme.radius, border: 'none',
  background: theme.orange, color: '#fff', cursor: 'pointer',
};

// Bouton secondaire (contour discret).
export const secondaryButtonStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 13, fontWeight: 500, padding: '8px 16px',
  borderRadius: theme.radius, border: `0.5px solid ${theme.borderStrong}`,
  background: theme.surface, color: theme.textSecondary, cursor: 'pointer',
};

export default theme;

// ============================================================================
// Helpers de SECTION (aspect uniforme pour toutes les cartes de FicheDossier)
// ============================================================================

// Carte de section — contour plus épais et identique partout.
export const sectionCardStyle = {
  background: theme.surface,
  borderRadius: theme.radiusLg,
  border: `1.5px solid ${theme.borderStrong}`,
  padding: 20,
  marginBottom: 16,
};

// Ligne d'en-tête de section (titre + icône à gauche, actions optionnelles à droite).
export const sectionHeaderRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 12, marginBottom: 14,
};

// Texte du titre de section (avec icône).
export const sectionTitleStyle = {
  fontSize: 14, fontWeight: 700, color: theme.text,
  display: 'flex', alignItems: 'center', gap: 9,
};

// Pastille d'icône de section (carré arrondi discret, icône orange).
export const sectionIconWrap = {
  width: 28, height: 28, borderRadius: 7, flexShrink: 0,
  background: theme.orangeFaint,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// Texte introductif sous un titre de section.
export const sectionIntroStyle = {
  fontSize: 12.5, color: theme.textSecondary, marginTop: -4, marginBottom: 16,
};

// Pied de section : zone des boutons, alignés en bas à droite.
export const sectionFooterStyle = {
  display: 'flex', justifyContent: 'flex-end', gap: 8,
  marginTop: 18, paddingTop: 16, borderTop: `1px solid ${theme.border}`,
};

// Bouton "Verrouiller / Déverrouiller" (état actif = ambre).
export function lockButtonStyle(locked, busy) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: theme.radius,
    cursor: busy ? 'not-allowed' : 'pointer',
    border: `1px solid ${locked ? '#F59E0B' : theme.borderStrong}`,
    background: locked ? '#FFFBEB' : theme.surface,
    color: locked ? '#92400E' : theme.textSecondary,
  };
}

// Jeu d'icônes de section — Tabler, stroke 1.75, aspect professionnel et cohérent.
// Chaque entrée est le contenu <path/> à placer dans un <svg viewBox="0 0 24 24">.
export const sectionIconPaths = {
  status:       '<path d="M9 12l2 2l4 -4" /><path d="M12 3a9 9 0 1 0 9 9a9 9 0 0 0 -9 -9" />',
  beneficiary:  '<path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" /><path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />',
  travel:       '<path d="M10.831 13.32l2.043 6.124a0.5 0.5 0 0 0 .948 0l4.097 -12.291a1 1 0 0 0 -1.265 -1.265l-12.291 4.097a0.5 0.5 0 0 0 0 .948l6.124 2.043" />',
  documents:    '<path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" /><path d="M9 9l1 0" /><path d="M9 13l6 0" /><path d="M9 17l6 0" />',
  pieces:       '<path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M9 12l2 2l4 -4" />',
  balance:      '<path d="M3 12h18" /><path d="M12 3v18" /><path d="M5 8h.01" /><path d="M5 16h.01" /><path d="M19 8h.01" /><path d="M19 16h.01" /><path d="M9 5a3 3 0 0 1 6 0" /><path d="M9 19a3 3 0 0 0 6 0" />',
  money:        '<path d="M12 3a3 3 0 0 0 -3 3v12a3 3 0 0 0 3 3" /><path d="M12 6h5a3 3 0 0 1 0 6h-5" /><path d="M12 12h5a3 3 0 0 1 0 6h-5" />',
  providers:    '<path d="M3 17h1m16 0h1m-13 0h6" /><path d="M5 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" /><path d="M15 17a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" /><path d="M5 17v-6a1 1 0 0 1 1 -1h9l4 4v3" /><path d="M13 10v-4h-7" />',
  validation:   '<path d="M3.5 5.5l1.5 1.5l2.5 -2.5" /><path d="M3.5 11.5l1.5 1.5l2.5 -2.5" /><path d="M3.5 17.5l1.5 1.5l2.5 -2.5" /><path d="M11 6l9 0" /><path d="M11 12l9 0" /><path d="M11 18l9 0" />',
  appel:        '<path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" /><path d="M12 17v-6" /><path d="M9.5 14.5l2.5 2.5l2.5 -2.5" />',
  lock:         '<path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" /><path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" /><path d="M8 11v-4a4 4 0 1 1 8 0v4" />',
  unlock:       '<path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" /><path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" /><path d="M8 11v-5a4 4 0 0 1 8 0" />',
};
